
import { useState } from "react";
import { Card } from "@/components/ui/card";
import { ExerciseLibrary } from "@/components/exercises/ExerciseLibrary";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { ArrowLeft, BookOpen, FilePlus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguagePath } from "@/utils/language";
import { CustomExerciseDialog } from "@/components/exercises/CustomExerciseDialog";
import { useAuth } from "@/components/AuthProvider";

export const ExerciseLibraryPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslations("dashboard-exercise-library");
  const { getPath } = useLanguagePath();
  const { user } = useAuth();

  const [showCustom, setShowCustom] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreated = () => setRefreshKey(k => k+1);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-4 border-b border-slate-100/50 dark:border-gray-700/50">
        <div className="space-y-1">
          <h2 className="text-3xl font-bold tracking-tight gradient-text flex items-center gap-2 dark:text-white">
            <BookOpen className="h-7 w-7 text-flame" />
            {t("title")}
          </h2>
          <p className="text-muted-foreground dark:text-gray-300">
            {t("subtitle")}
          </p>
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80 dark:text-flame">
                  {t("breadcrumb.dashboard")}
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage className="font-medium dark:text-white">
                  {t("breadcrumb.exerciseLibrary")}
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
        <div className="flex gap-2">
          {user && (
            <>
              <Button variant="default" onClick={() => setShowCustom(true)} className="dark:text-white flex">
                <FilePlus className="mr-2 h-4 w-4" />
                {t("custom.createButton")}
              </Button>
            </>
          )}
          <Button variant="outline" onClick={() => navigate(-1)} className="ml-auto dark:text-white dark:border-gray-600 dark:hover:bg-gray-700">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("back")}
          </Button>
        </div>
      </div>
      <CustomExerciseDialog open={showCustom} onClose={() => setShowCustom(false)} onCreated={handleCreated} />
      <Card key={refreshKey} className="bg-white/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 p-6 dark:bg-gray-800/90 dark:border-gray-700/50">
        <ExerciseLibrary key={refreshKey} showCustomExercises />
      </Card>
    </div>
  );
};
